import { useNavigate, useRouter } from '@tanstack/react-router'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { getConsoleLog } from '@/lib/consoleCapture'

interface GeneralErrorProps extends React.HTMLAttributes<HTMLDivElement> {
  minimal?: boolean
  /** Provided by TanStack Router when used as a route errorComponent. */
  error?: unknown
}

export default function GeneralError({
  className,
  minimal = false,
  error,
}: GeneralErrorProps) {
  const navigate = useNavigate()
  const { history } = useRouter()
  // dev-nest diagnostic: surface the actual error so crashes are debuggable
  // without DevTools. (Only shown on dev-nest hostnames.)
  const isDevNest =
    typeof window !== 'undefined' &&
    /dev-?nest|trycloudflare|localhost/i.test(window.location.hostname)
  const err = error as { message?: string; stack?: string } | undefined
  const detail = err?.stack || err?.message || (error ? String(error) : '')

  // Full report text (error + recent console log) — used verbatim by Copy.
  const fullReport = () =>
    `## Crash Report\n\n` +
    `**URL:** ${window.location.href}\n` +
    `**Time:** ${new Date().toISOString()}\n` +
    `**User Agent:** ${navigator.userAgent}\n\n` +
    `### Error\n\n\`\`\`\n${detail || 'No error detail captured'}\n\`\`\`\n\n` +
    `### Console log (recent)\n\n\`\`\`\n${getConsoleLog() || '(empty)'}\n\`\`\`\n`

  const reportCrash = () => {
    const title = encodeURIComponent(
      `[Crash] ${(err?.message || 'Unhandled error').slice(0, 120)}`
    )
    // GitHub prefilled-issue URLs are length-capped (~8KB), so attach a trimmed
    // tail of the console log; the Copy button carries the full log.
    const body = encodeURIComponent(
      `## Crash Report\n\n` +
        `**URL:** ${window.location.href}\n` +
        `**Time:** ${new Date().toISOString()}\n` +
        `**User Agent:** ${navigator.userAgent}\n\n` +
        `### Error\n\n\`\`\`\n${(detail || 'No error detail captured').slice(0, 2500)}\n\`\`\`\n\n` +
        `### Console log (recent, trimmed — use "Copy crash details" for the full log)\n\n` +
        '```\n' +
        getConsoleLog(3000) +
        '\n```\n'
    )
    window.open(
      `https://github.com/Phenom-earth/phenom-backend/issues/new?title=${title}&body=${body}&labels=bug,crash`,
      '_blank'
    )
  }
  const copyDetails = () => {
    void navigator.clipboard?.writeText(fullReport()).catch(() => {})
  }
  return (
    <div className={cn('h-svh w-full', className)}>
      <div className='m-auto flex h-full w-full flex-col items-center justify-center gap-2'>
        {!minimal && (
          <h1 className='text-[7rem] leading-tight font-bold'>500</h1>
        )}
        <span className='font-medium'>Oops! Something went wrong {`:')`}</span>
        <p className='text-muted-foreground text-center'>
          We apologize for the inconvenience. <br /> Please try again later.
        </p>
        {!minimal && (
          <div className='mt-6 flex gap-4'>
            <Button variant='outline' onClick={() => history.go(-1)}>
              Go Back
            </Button>
            <Button onClick={() => navigate({ to: '/' })}>Back to Home</Button>
          </div>
        )}
        {detail && (
          <div className='mt-4 flex flex-wrap justify-center gap-3'>
            <Button variant='outline' onClick={copyDetails}>
              Copy crash details
            </Button>
            <Button onClick={reportCrash}>🐛 Report crash on GitHub</Button>
          </div>
        )}
        {isDevNest && detail && (
          <pre
            style={{
              marginTop: 20,
              maxWidth: '90vw',
              maxHeight: '40vh',
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              fontSize: 11,
              lineHeight: 1.4,
              color: '#ff9a9a',
              background: 'rgba(0,0,0,0.5)',
              border: '1px solid rgba(255,90,90,0.4)',
              borderRadius: 8,
              padding: 12,
              textAlign: 'left',
            }}
          >
            {detail}
          </pre>
        )}
      </div>
    </div>
  )
}
